import { sql } from 'drizzle-orm';
import { index, pgTable, text, timestamp, uniqueIndex, uuid, varchar } from 'drizzle-orm/pg-core';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// DECLARED ANSWERS — THE DECLARED-INPUTS LAW (D19, supersedes D17's framing).
// docs/design/fix-architecture-2026-07-08/{FIX-ARCHITECTURE.md,BUILD-PLAN.md},
// Class 8 / Phase 2 Lane D. SQL twin:
// docs/design/fix-architecture-2026-07-08/phase2a-declared-answers-neon.sql.
//
// Jason, verbatim (D19): "These need to be layers of dynamics accepted by an
// engine that understands the complexities of each user based off a dynamic
// list of static answers: Where are you? Who are you? What's your home
// country? What are you looking for? Where do you live?... There must be a
// thing that says enter your home address to find targeted jobs near you, or
// your postal code."
//
// A DYNAMIC LIST, not a fixed column set: one row per (user, questionKey) —
// adding a new declarable question (e.g. "what_youre_looking_for") never
// requires a migration, only a new questionKey value. questionKey is DATA,
// not code (same discipline as coverageHub's topicKey discriminated string).
//
// NO INVENTION: a missing row IS the honest "undeclared" state — callers
// (eligibility.ts's inferTargetCountry, the Jobs board's missing-input gate)
// read this table FIRST and fall back to their existing coarse/derived
// behaviour only when no row exists, surfaced as "unconfirmed," never
// presented as a declared fact.
//
// Append-then-read-latest (mirrors claim_corrections' terminal-per-write
// shape, NOT a destructive update): a re-declared answer OVERWRITES via
// onConflictDoUpdate — the unique index is on (userId, questionKey), so
// "the current answer for this key" is always a single indexed read — while
// `source` + `answeredAt` still let a caller see this was a per-key value,
// matching corpus_coverage_state's own "compiled latest state, not an
// append-only ledger" precedent.
// =============================================================================

/** Known question keys this pass declares — NOT an exhaustive enum at the DB
 *  level (questionKey is `varchar`, data not a schema fork, so a new question
 *  never needs a migration) but a typed reference set for callers that DO
 *  know which keys they read/write today. */
export const DECLARED_ANSWER_KEYS = [
  'home_country',
  'home_postal_code',
  'home_metro',
  'identity',
  'what_youre_looking_for',
] as const;

export type DeclaredAnswerKey = (typeof DECLARED_ANSWER_KEYS)[number];

/** Where a declared answer came from — honest provenance, never inferred to
 *  be the same weight as a direct answer. 'user_declared' = the person typed
 *  it in response to an explicit ask (the D19 banner/loop); 'onboarding' =
 *  captured during initial setup; 'corpus_inferred' reserved for a future
 *  loop-derived suggestion the person then confirms (NOT written by this
 *  pass — Phase 2a only ships the direct-declare path). */
export type DeclaredAnswerSource = 'user_declared' | 'onboarding' | 'corpus_inferred';

export const declaredAnswers = pgTable(
  'declared_answers',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    questionKey: varchar('question_key', { length: 64 }).notNull(),
    answer: text('answer').notNull(),
    source: varchar('source', { length: 24 }).notNull().default('user_declared'),
    answeredAt: timestamp('answered_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    // ONE current answer per (user, key) — a re-declare overwrites via
    // onConflictDoUpdate, never accumulates duplicate rows for the same key.
    userKeyIdx: uniqueIndex('declared_answers_user_key_idx').on(t.userId, t.questionKey),
    userIdx: index('declared_answers_user_idx').on(t.userId),
  }),
);
